import React, { useState, useEffect, useRef } from 'react';
// import { appInsights, reactPlugin } from '../../appInsights';
// import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { SelectChangeEvent, Typography } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import cn from 'clsx';
import FormControlLabel from '@mui/material/FormControlLabel';
import { TextComponent, SelectComponent } from '../../uiComponents';
import { jsonEditorStyles } from '../jsonEditorStyles';
import { useSelector, useDispatch } from 'react-redux';
import {
  getJSONClaims,
  getJSONAADAttributes,
  setJSONClaims,
  setJSONAADAttributes,
  getJSONSession,
  setJSONSSOSession,
  getNodeData,
  setNodeData,
} from '../../../store';
import CONSTANTS from '../../../constants/constants';
import JSON_CONSTANTS from '../../../constants/jsonConstants';
import { OptionProps } from '../../../view-models';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { FieldMapperRow } from './fieldMapperRow';
import useComponentUIHook from './useComponentUIHook';
import { cloneDeep, isEqual } from 'lodash';

const stylesLocal = makeStyles((theme: Theme) => {
  return createStyles({
    claimEntry: {
      borderBottom: '1px solid grey',
      padding: 10,
    },
    marginRight: { marginRight: '20px' },
  });
});

interface SSOSessionClaimRef {
  claimId: string;
  defaultValue: string;
}

interface SSOSessionClaims {
  persistedClaims: SSOSessionClaimRef[];
  outputClaims: SSOSessionClaimRef[];
}

type SessionEditingMode = 'add' | 'edit';

interface componentProps {
  componentData: any;
  closeGritComponent: any;
  setUnsavedChanges: any;
}

export const GenerateSessionExistsUI = ({ componentData, closeGritComponent, setUnsavedChanges }: componentProps) => {
  const { modifiedData, allClaims } = useComponentUIHook(componentData);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(true);
  const classes = jsonEditorStyles();
  const ssoSession = useSelector(getJSONSession);

  const [ssoSessionClaims, setSSOSessionClaims] = useState<SSOSessionClaims>(
    ssoSession ?
      {
        persistedClaims: [...ssoSession.persistedClaims],
        outputClaims: [...ssoSession.outputClaims]
      } :
      {
        persistedClaims: [],
        outputClaims: []
      }
  );
  const [nextPersistedClaim, setNextPersistedClaim] = useState<SSOSessionClaimRef>({
    claimId: '',
    defaultValue: '',
  });
  const [nextOutputClaim, setNextOutputClaim] = useState<SSOSessionClaimRef>({
    claimId: '',
    defaultValue: '',
  });

  const dispatch = useDispatch();

  useEffect(() => {
    let _res = isEqual(ssoSessionClaims.persistedClaims, ssoSession.persistedClaims) &&
    isEqual(ssoSessionClaims.outputClaims, ssoSession.outputClaims)
    setDisableSubmit(_res);
    setUnsavedChanges(_res);
  }, [modifiedData, ssoSessionClaims, ssoSession]);

  return (
    <div className={classes.flexRow}>
      <div className={classes.flexCol}>
        <Typography variant="body1">Claims stored in session cookie:</Typography>
        {ssoSessionClaims.persistedClaims.map((pClm, index) => {
          return (
            <FieldMapperRow
              key={index}
              rowData={pClm}
              writeback={(rowData: any) => {
                const _newPersistedClaims = ssoSession.persistedClaims.slice();
                _newPersistedClaims[index] = rowData;
                setSSOSessionClaims({
                  ...ssoSessionClaims,
                  persistedClaims: _newPersistedClaims,
                });
              }}
              claimsList={Object.keys(allClaims)
                .filter((clmId: any) => {
                  return (
                    clmId === pClm.claimId ||
                    (!ssoSessionClaims.persistedClaims.some((opClm) => {
                      return opClm.claimId === clmId;
                    }) &&
                      !ssoSessionClaims.outputClaims.some((ooClm) => {
                        return ooClm.claimId === clmId;
                      }))
                  );
                })
                .map((clmId: any) => {
                  return { label: clmId, value: clmId };
                })}
              allClaims={allClaims}
              enabledSources={['claim']}
              sourceFieldName="claimId"
              sourceFieldLabels={{ claim: 'Claim' }}
              fixedSourceType="claim"
              defaultValueFieldName="defaultValue"
              defaultValueFieldLabel="Default Value"
              allowEmptyDefaultValue={true}
              buttonText="Delete"
              buttonAction={() => {
                const newPCs = ssoSessionClaims.persistedClaims.filter((ignored, idx) => {
                  return index !== idx;
                });
                setSSOSessionClaims({
                  ...ssoSessionClaims,
                  persistedClaims: newPCs,
                });
              }}
            />
          );
        })}
        <FieldMapperRow
          rowData={nextPersistedClaim}
          writeback={(rowData: any) => {
            setNextPersistedClaim({
              ...rowData,
            });
          }}
          claimsList={Object.keys(allClaims)
            .filter((clmId: any) => {
              return (
                !ssoSessionClaims.persistedClaims.some((pClm) => {
                  return pClm.claimId === clmId;
                }) &&
                !ssoSessionClaims.outputClaims.some((oClm) => {
                  return oClm.claimId === clmId;
                })
              );
            })
            .map((clmId: string) => {
              return { label: clmId, value: clmId };
            })}
          allClaims={allClaims}
          enabledSources={['claim']}
          sourceFieldName="claimId"
          sourceFieldLabels={{ claim: 'Claim' }}
          fixedSourceType="claim"
          defaultValueFieldName="defaultValue"
          defaultValueFieldLabel="Default Value"
          allowEmptyDefaultValue={true}
          buttonText="Add"
          buttonAction={() => {
            const newPCs = ssoSessionClaims.persistedClaims.slice();
            newPCs.push({ claimId: nextPersistedClaim.claimId, defaultValue: nextPersistedClaim.defaultValue });
            setSSOSessionClaims({
              ...ssoSessionClaims,
              persistedClaims: newPCs,
            });
            setNextPersistedClaim({
              claimId: '',
              defaultValue: '',
            });
          }}
          buttonDisabled={!nextPersistedClaim.claimId}
        />
        <Typography variant="body1">New claims output by session cookie:</Typography>
        {ssoSessionClaims.outputClaims.map((oClm, index) => {
          return (
            <FieldMapperRow
              key={index}
              rowData={oClm}
              writeback={(rowData: any) => {
                const _newOutputClaims = ssoSessionClaims.outputClaims.slice();
                _newOutputClaims[index] = rowData;
                setSSOSessionClaims({
                  ...ssoSessionClaims,
                  outputClaims: _newOutputClaims,
                });
              }}
              claimsList={Object.keys(allClaims)
                .filter((clmId: any) => {
                  return (
                    clmId === oClm.claimId ||
                    (!ssoSessionClaims.persistedClaims.some((opClm) => {
                      return opClm.claimId === clmId;
                    }) &&
                      !ssoSessionClaims.outputClaims.some((ooClm) => {
                        return ooClm.claimId === clmId;
                      }))
                  );
                })
                .map((clmId: any) => {
                  return { label: clmId, value: clmId };
                })}
              allClaims={allClaims}
              enabledSources={['claim']}
              sourceFieldName="claimId"
              sourceFieldLabels={{ claim: 'Claim' }}
              fixedSourceType="claim"
              defaultValueFieldName="defaultValue"
              defaultValueFieldLabel="Value"
              allowEmptyDefaultValue={true}
              buttonText="Delete"
              buttonAction={() => {
                const newOCs = ssoSessionClaims.outputClaims.filter((ignored, idx) => {
                  return index !== idx;
                });
                setSSOSessionClaims({
                  ...ssoSessionClaims,
                  outputClaims: newOCs,
                });
              }}
            />
          );
        })}
        <FieldMapperRow
          rowData={nextOutputClaim}
          writeback={(rowData: any) => {
            setNextOutputClaim({
              ...rowData,
            });
          }}
          claimsList={Object.keys(allClaims)
            .filter((clmId: any) => {
              return (
                !ssoSessionClaims.persistedClaims.some((pClm) => {
                  return pClm.claimId === clmId;
                }) &&
                !ssoSessionClaims.outputClaims.some((oClm) => {
                  return oClm.claimId === clmId;
                })
              );
            })
            .map((clmId: string) => {
              return { label: clmId, value: clmId };
            })}
          allClaims={allClaims}
          enabledSources={['claim']}
          sourceFieldName="claimId"
          sourceFieldLabels={{ claim: 'Claim' }}
          fixedSourceType="claim"
          defaultValueFieldName="defaultValue"
          defaultValueFieldLabel="Value"
          allowEmptyDefaultValue={true}
          buttonText="Add"
          buttonAction={() => {
            const newOCs = ssoSessionClaims.outputClaims.slice();
            newOCs.push({
              claimId: nextOutputClaim.claimId,
              defaultValue: nextOutputClaim.defaultValue,
            });
            setSSOSessionClaims({
              ...ssoSessionClaims,
              outputClaims: newOCs,
            });
            setNextOutputClaim({
              claimId: '',
              defaultValue: '',
            });
          }}
          buttonDisabled={!nextOutputClaim.claimId}
        />
        <div>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={disableSubmit}
            sx={{ mt: 3, mb: 2 }}
            onClick={() => {
              dispatch(setJSONSSOSession({
                ...ssoSession,
                persistedClaims: [...ssoSessionClaims.persistedClaims],
                outputClaims: [...ssoSessionClaims.outputClaims],
                components: ssoSession.components ? [...ssoSession.components] : []
              }));
              closeGritComponent(cloneDeep(modifiedData));
            }}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};
